import React from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  alpha,
  useTheme
} from '@mui/material';
import {
  MoreVert,
  Edit,
  CheckCircleOutline,
  PowerSettingsNew,
  Inventory2Outlined,
  ChevronRight
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Session } from '../../types/sessions';
import StatusChip from '../common/StatusChip';

interface SessionsTableProps {
  sessions: Session[];
  onActivate: (session: Session) => void;
  onComplete: (session: Session) => void;
  onArchive: (session: Session) => void;
  onEdit: (session: Session) => void;
  onOpen: (session: Session) => void;
}

const SessionsTable: React.FC<SessionsTableProps> = ({
  sessions, onActivate, onComplete, onArchive, onEdit, onOpen
}) => {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [menuSession, setMenuSession] = React.useState<Session | null>(null);

  const openMenu = (e: React.MouseEvent<HTMLElement>, session: Session) => {
    setMenuAnchor(e.currentTarget);
    setMenuSession(session);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuSession(null);
  };

  if (sessions.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
      <Table size="medium">
        <TableHead>
          <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.025) }}>
            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Session</TableCell>
            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Dates</TableCell>
            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Status</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sessions.map((s) => {
            const canActivate = s.status === 'planning';
            const canComplete = s.status === 'active';
            const canArchive  = s.status === 'completed' || s.status === 'planning';
            const canEdit     = s.status === 'planning' || s.status === 'active';

            return (
              <TableRow
                key={s._id}
                hover
                sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}
                onClick={() => onOpen(s)}
              >
                <TableCell>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Box
                      sx={{
                        width: 6, height: 36, borderRadius: 1,
                        bgcolor:
                          s.status === 'active'    ? 'success.main' :
                          s.status === 'planning'  ? 'text.disabled' :
                          s.status === 'completed' ? 'info.main'    :
                                                     'text.disabled'
                      }}
                    />
                    <Box>
                      <Typography variant="body1" fontWeight={600}>{s.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Created {format(new Date(s.createdAt), 'MMM d, yyyy')}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {format(new Date(s.startDate), 'MMM d, yyyy')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    → {format(new Date(s.endDate), 'MMM d, yyyy')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <StatusChip status={{ kind: 'session', value: s.status }} />
                </TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <IconButton size="small" onClick={() => onOpen(s)} sx={{ mr: 0.5 }}>
                    <ChevronRight fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={(e) => openMenu(e, s)}>
                    <MoreVert fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 200 } } }}
      >
        {menuSession?.status === 'planning' && (
          <MenuItem onClick={() => { if (menuSession) onActivate(menuSession); closeMenu(); }}>
            <ListItemIcon><PowerSettingsNew fontSize="small" color="success" /></ListItemIcon>
            <ListItemText primary="Activate" secondary="Make this the active session" />
          </MenuItem>
        )}
        {menuSession?.status === 'active' && (
          <MenuItem onClick={() => { if (menuSession) onComplete(menuSession); closeMenu(); }}>
            <ListItemIcon><CheckCircleOutline fontSize="small" /></ListItemIcon>
            <ListItemText primary="Mark complete" secondary="Lock this session" />
          </MenuItem>
        )}
        {(menuSession?.status === 'planning' || menuSession?.status === 'active') && (
          <MenuItem onClick={() => { if (menuSession) onEdit(menuSession); closeMenu(); }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
            <ListItemText primary="Edit name & dates" />
          </MenuItem>
        )}
        {(menuSession?.status === 'completed' || menuSession?.status === 'planning') && (
          <>
            <Divider />
            <MenuItem onClick={() => { if (menuSession) onArchive(menuSession); closeMenu(); }}>
              <ListItemIcon><Inventory2Outlined fontSize="small" /></ListItemIcon>
              <ListItemText primary="Archive" secondary="Hide from default list" />
            </MenuItem>
          </>
        )}
        {/* Suppress unused-variable lint */}
        {!canMenuAction(menuSession) && (
          <MenuItem disabled>
            <ListItemText primary="No actions for archived sessions" />
          </MenuItem>
        )}
      </Menu>
    </Paper>
  );
};

const canMenuAction = (s: Session | null) =>
  !!s && (s.status === 'planning' || s.status === 'active' || s.status === 'completed');

export default SessionsTable;
